import React from "react";
import { ElegantTooltip } from "../../../shared/formComponents/ElegantTooltip/ElegantTooltip";
import "./_wig-link-chip.scss";

// ═══════════════════════════════════════════════════════════════════════════
// WigLinkChip — which Year 1 WIG a company Objective was drafted from.
//
// ⚠️ BYTE-IDENTICAL IN BOTH REPOS, like `ObjectiveDetails`. `diff` is the test:
//
//   10x-frontend/.../UserPanel/AnnualOkr/_shared/WigLinkChip.js
//   10x-coach-frontend/.../CoachPanel/AnnualOkr/_shared/WigLinkChip.js
//
// It sits on the header of every company-Objective accordion — the CEO's
// interview board (and its read-only twin on the coach-waiting screen), the
// CEO's coach-remarks panel, the coach's review, and the live review on both
// sides — so the same Objective carries the same link wherever it is shown.
// The pattern is Quarterly OKR's `LinkChip`, details in an ElegantTooltip.
//
// ⚠️ ICON ONLY, BY DESIGN (2026-10-08). The first version printed the WIG name
// in the chip ("🔗 Financial Milestone"); beside the statement, the status pill
// and the chevron it cluttered every accordion header, so the name moved into
// the tooltip. Don't put it back in the chip. Screen readers still get it from
// the `visually-hidden` text below.
//
// ⚠️ THREE STATES, AND `undefined` IS NOT `null`.
//   string     → linked: link icon; the WIG's name is in the tooltip.
//   null       → freestanding (§5.6: "null = freestanding"): dashed
//                link-slash icon, "Not linked" in the tooltip.
//   undefined  → this endpoint didn't send `wig_label` at all: render NOTHING.
// Collapsing the last two would tell the CEO a WIG-sourced Objective is "Not
// linked" on any screen whose payload simply omits the field — the contract's
// §4.6 tracking shape, for one, does not list it. Absent data is said by
// absence, not by a wrong answer.
//
// ⚠️ ONLY THE LABEL. The payloads carry `wig_label` and
// `execution_blueprint_item_id`, nothing else about the WIG — no owner, no
// pillar — so the tooltip shows only the eyebrow and the name. It used to add a
// sentence explaining the link; that was dropped (2026-10-12) to keep the
// tooltip short. If the server ever sends more, the tooltip is the place.
//
// Accessibility, from the review of QOKR's chip (2026-10-08):
//   • the description is real text in a `visually-hidden` span, not an
//     `aria-label` — ARIA prohibits `aria-label` on a role-less span, so screen
//     readers ignore it. Real text also works when the chip sits INSIDE a row's
//     header <button> (`focusable={false}`): it joins the button's name, so a
//     keyboard user still learns the link without reaching the tooltip;
//   • tooltip colours clear 4.5:1 (the ElegantTooltip teal #08b3c3 is ~2.6:1
//     on white, so the eyebrow uses #056C75 instead).
// ═══════════════════════════════════════════════════════════════════════════

const WigTip = ({ linked, wigLabel }) => (
  <span className="aowig-tip">
    <span className="aowig-tip__eyebrow">
      {linked ? "Linked WIG" : "Not linked"}
    </span>
    <span className="aowig-tip__title">
      {linked ? wigLabel : "This objective isn't linked to a WIG."}
    </span>
    {/* No explanatory note under the title (removed 2026-10-12, by
        instruction): eyebrow + WIG name is the whole tooltip. */}
  </span>
);

const WigLinkChip = ({
  // `objective.wig_label`, passed straight through — see the three states
  // above. Do not default it to null at the call site.
  wigLabel,
  placement = "top",
  // Off when the chip sits inside a row's header <button>: a focus stop nested
  // in a button is invalid HTML. Hover still opens the tooltip, and the hidden
  // text below still reaches screen readers through the button's name.
  focusable = true,
}) => {
  if (wigLabel === undefined) return null;

  const linked = !!wigLabel;

  return (
    <ElegantTooltip
      content={<WigTip linked={linked} wigLabel={wigLabel} />}
      placement={placement}
      maxWidth={280}
    >
      <span
        className={`aowig${linked ? "" : " aowig--none"}`}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={focusable ? 0 : undefined}
      >
        <i
          className={`fa-solid ${linked ? "fa-link" : "fa-link-slash"}`}
          aria-hidden="true"
        />
        {/* The only text in the chip, and invisible: the icon carries the
            meaning by sight, this carries it by ear. */}
        <span className="visually-hidden">
          {linked ? `Linked WIG: ${wigLabel}` : "Not linked to a WIG"}
        </span>
      </span>
    </ElegantTooltip>
  );
};

export default WigLinkChip;
